/**
 * @file Select Account Identity component.
 */

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';

import IdentityAvatarContainer from '../../containers/global/identity/IdentityAvatarContainer';
import IdentityNameContainer from '../../containers/global/identity/IdentityNameContainer';
import AngleRightIcon from '../icons/AngleRightIcon';
import { UPDATE_ACCOUNT_IDENTITY_MODAL } from '../../constants/modals';
import ContextualString from '../contextual/ContextualString';
import { AVATAR_SIZE_42 } from '../../constants/avatar-sizes';
import EditIcon from '../icons/EditIcon';
import Badge from '../global/Badge';
import AlbumSelectionButton from './AlbumSelectionButton';
import { IDENTITY_RELATIONSHIP } from '../../constants/identity';

class SelectAccountIdentity extends Component {
  static propTypes = {
    identity: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      relationship: PropTypes.string,
      current_photo: PropTypes.object,
      has_outdated_selfie: PropTypes.bool,
    }),
    selectedIdentities: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        selectedAlbums: PropTypes.array,
      }),
    ),
    onSelectIdentity: PropTypes.func.isRequired,
    uploadedPhoto: PropTypes.object,
    openModal: PropTypes.func.isRequired,
    onShowSelection: PropTypes.func,
    requiresPurchase: PropTypes.bool,
    onPurchase: PropTypes.func,
    requiresGender: PropTypes.bool,
    identityFilter: PropTypes.oneOf(Object.values(IDENTITY_RELATIONSHIP)),
  };

  onIdentitySelected = event => {
    event?.preventDefault?.();

    const {
      identity,
      uploadedPhoto,
      onSelectIdentity,
      openModal,
      requiresPurchase,
      onPurchase,
      requiresGender,
    } = this.props;

    if (requiresPurchase) {
      onPurchase(identity);
      return;
    }

    if (!identity.current_photo && !uploadedPhoto) {
      openModal(UPDATE_ACCOUNT_IDENTITY_MODAL, {
        identity,
        forceSelfieUpdate: true,
      });
      return;
    }

    // `updateSelfie` request is not sync in nature, i.e.
    // the field identity.has_outdated_selfie will return `true`
    // even if we update the selfie, so to check if a user
    // really has an outdated selfie we also need to check
    // our local redux store for recent uploads for this identity,
    // if there's an `uploadedPhoto` it means that the user just
    // uploaded a selfie (which by definition is not considered outdated)
    if (identity.has_outdated_selfie && !uploadedPhoto) {
      openModal(UPDATE_ACCOUNT_IDENTITY_MODAL, {
        identity,
        outdatedSelfie: true,
      });
      return;
    }

    if (!identity.gender && requiresGender) {
      openModal(UPDATE_ACCOUNT_IDENTITY_MODAL, {
        identity,
        outdatedSelfie: false,
        forceSelfieUpdate: false,
      });
      return;
    }

    onSelectIdentity(identity);
  };

  onEditIdentity = event => {
    event.preventDefault();
    event.stopPropagation();

    const { identity, uploadedPhoto, openModal } = this.props;

    openModal(UPDATE_ACCOUNT_IDENTITY_MODAL, {
      identity,
      forceSelfieUpdate:
        (!identity.current_photo || identity.has_outdated_selfie) &&
        !uploadedPhoto,
      outdatedSelfie: identity.has_outdated_selfie && !uploadedPhoto,
    });
  };

  componentDidMount() {
    const { identity, identityFilter } = this.props;
    if (
      identity.relationship === IDENTITY_RELATIONSHIP.PRIMARY &&
      identityFilter === IDENTITY_RELATIONSHIP.PRIMARY
    ) {
      this.onIdentitySelected();
    }
  }

  render() {
    const { identity, selectedIdentities, onShowSelection, requiresPurchase } =
      this.props;

    const selectedIdentity = selectedIdentities.find(
      ({ id }) => id === identity.id,
    );
    const selectedAlbums = selectedIdentity && selectedIdentity.selectedAlbums;
    const [album] = selectedAlbums || [];

    return (
      <a
        href="#"
        className="identity-container"
        onClick={this.onIdentitySelected}
      >
        <IdentityAvatarContainer
          identity={identity}
          className="mr-2"
          onClick={this.onEditIdentity}
          size={AVATAR_SIZE_42}
        >
          <span className="btn-identity-edit blue right">
            <EditIcon color="white" />
          </span>
        </IdentityAvatarContainer>

        <IdentityNameContainer
          className="text-dark text-extra-bold mr-1"
          identity={identity}
          defaultName="Find photos of me"
        />

        <div className="ml-auto d-flex align-items-center text-right">
          {selectedAlbums && !!selectedAlbums.length && (
            <span className="text-xs text-color-base">
              {onShowSelection ? (
                <AlbumSelectionButton
                  identity={selectedIdentity}
                  onClick={e => {
                    e.stopPropagation();
                    onShowSelection(selectedIdentity);
                  }}
                  className="mr-1"
                />
              ) : (
                <Fragment>
                  {selectedAlbums.length}{' '}
                  <ContextualString
                    id="consumerAlbum"
                    defaultMessage="album"
                    type={album && album.album_type}
                    subtype={album && album.album_subtype}
                    formatter={string =>
                      pluralize(string, selectedAlbums.length)
                    }
                  />{' '}
                  selected
                </Fragment>
              )}
            </span>
          )}

          {requiresPurchase && <Badge badgeSize="sm">Purchase</Badge>}
          {!requiresPurchase && (
            <AngleRightIcon color="#1983fb" height="18px" className="ml-1" />
          )}
        </div>
      </a>
    );
  }
}

export default SelectAccountIdentity;
